/* GW_SOURCE_OF_TRUTH
  file: src/App.tsx
  shortSHA: C515CD5A505147
  pinnedAt: 2025-09-08
*/
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */


console.debug("[App] module loaded @", new Date().toISOString());
// Lås- och heartbeat-parametrar (stabilare för flera testare)
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Debug toggle to quiet console noise from forceLogout; set true to enable logs
const DEBUG_FORCELOG = false;
const dbg = (...args: any[]) => { if (DEBUG_FORCELOG) console.debug(...args); };

const isCustomer = (u: { role?: unknown } | null | undefined): boolean =>
  String((u as any)?.role) === "customer";


//import EditModal from "./components/edit/EditModal";


// React & DOM
import React, { useEffect, useMemo, useRef, useState, useCallback, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { parseLooseDateToISO } from "./lib/dateUtils";

// Pages
import InvoicingPage from "./pages/InvoicingPage";
import ProductTypesAdmin from "./pages/ProductTypesAdmin";
import UserAdmin from "./pages/UserAdmin";
import { generateInvoiceReportForMarkedItems } from "./lib/billing";
const BugBoard = lazy(() => import("./pages/BugBoard"));

// Components
import EditModal from "./components/EditModal";
import { TopBarActions } from "./components/TopBarActions";
import { SideMenu } from "./components/SideMenu";
export { gwSetUserClaims } from "./services/claimsClient";

// Services & Lib
import { ensureProductTypeInDb, loadProductTypesForImpact } from "./services/productTypes";
import { createInvoiceReportCF } from "./services/invoicingClient";
import { computeBillingSteps } from "./lib/billing";
import {
  computeOverallProgress,
  createHandlePhotoChange,
  createHandleRemovePhoto,
  PhotoURLMap,
  ThumbnailMap,
} from "./services/photoUploads";
import { useWizardNavigation } from "./hooks/useWizardNavigation";
import { useCreateItemFlow } from "./hooks/useCreateItemFlow";


// Firebase app bindings
import { auth, db, storage } from "./firebase";
import "./devTools/seedProductTypes";

// Firebase — Firestore (types)
import type {
  UpdateData,
} from "firebase/firestore";

// Firebase — Firestore (values)
import {
  arrayUnion,
  doc,
  updateDoc,
} from "firebase/firestore";

// Firebase — Storage
// (only used via services/devCleanup)

// Firebase — Auth & Functions

// Styles
import "./styles.css";

import useForceLogoutOnClaimsBump from "./hooks/useForceLogoutOnClaimsBump";
import LogoutNotice from "./components/LogoutNotice";
import { PAGE_SIZE } from "./styles/constants";
import { fmtDateOnly } from "./lib/formatters";
import { resolveProductTypeSelection } from "./lib/productTypeSelection";
import {
  formatSerialForDisplay,
  toEpochMillis,
} from "./lib/inventoryHelpers";
import { type PageKey, pageKeyToPath, pathToPage, reportsViewFromPath } from "./lib/menu";
import { getCurrentUserString } from "./lib/currentUser";
import { useInventoryList } from "./hooks/useInventoryList";
import { useAuthState } from "./hooks/useAuthState";
import { useDraftWizard } from "./hooks/useDraftWizard";
import { useBillingList } from "./hooks/useBillingList";
import { useDeleteActions } from "./hooks/useDeleteActions";
import { useReportHash } from "./hooks/useReportHash";
import { useClaimsBumpNotice } from "./hooks/useClaimsBumpNotice";
import { useCustomerPortalGuard } from "./hooks/useCustomerPortalGuard";
import { useExposeFetchFirstPage } from "./hooks/useExposeFetchFirstPage";
import { useDebugGate } from "./hooks/useDebugGate";
import { useIsoDateMask } from "./hooks/useIsoDateMask";
import { useEditItem } from "./hooks/useEditItem";
import { useOpenEdit } from "./hooks/useOpenEdit";
import { useArticlesByCustomer } from "./hooks/useArticlesByCustomer";
import { useDirectoryLists } from "./hooks/useDirectoryLists";
import { useEditLocks } from "./hooks/useEditLocks";
import { useVisibleItems } from "./hooks/useVisibleItems";
import { useAppShell } from "./hooks/useAppShell";
import { useSaveEdit } from "./hooks/useSaveEdit";
import { useEditLiveSync } from "./hooks/useEditLiveSync";
import { useEditCompletion, useEditReopen } from "./hooks/useEditCompletion";
import { useInvoiceToggle } from "./hooks/useInvoiceToggle";
import { useToggleDeleteFromList } from "./hooks/useToggleDeleteFromList";
import { useAiSuggestions } from "./hooks/useAiSuggestions";
import { useCustomerCards } from "./hooks/useCustomerCards";
import { useCustomerOrderNumbers } from "./hooks/useCustomerOrderNumbers";
import {
  callVendorLookup,
  suggestItemDetailsCallable,
  buildWarrantyLink,
  copyToClipboard,
  registerVendorDevHelpers,
} from "./services/vendorHelpers";
import { createWarrantyActions } from "./services/warrantyActions";
import { HomeQuickView } from "./views/home/HomeQuickView";
import { HomeShell } from "./views/home/HomeShell";
import { AppShellLayout } from "./components/AppShellLayout";
import { CustomersPage } from "./pages/CustomersPage";
import { CertificateResultModal } from "./components/CertificateResultModal";

import {
  toMillis,
  LOCK_STALE_MS,
} from "./components/editModalFunctions";
import { toKey } from "./components/editModalFunctions";
import {
  splitSerialParts,
  normalizeSerialKey,
  buildDisplaySerial,
} from "./services/serialIndex";
import { ReportsView } from "./views/ReportsView";
import { VidareforsaljningView } from "./views/VidareforsaljningView";
import { InventoryListView } from "./views/home/InventoryListView";
import { HomeWizardView } from "./views/home/HomeWizardView";
import { AuthGate } from "./components/AuthGate";
import { ReportViewRouter } from "./components/ReportViewRouter";
import { CertificatesPage } from "./pages/CertificatesPage";

import { createTestItemViaPrompt } from "./devTools/createTestItem";
import { useDevTools } from "./hooks/useDevTools";
import { useInventoryFilters } from "./hooks/useInventoryFilters";
import { useReportFilters } from "./hooks/useReportFilters";
import { createEmptyRegistrationForm } from "./lib/registrationForm";
// håll koll var 5–60 s, och alltid < stale/2 för att förnya lås i tid
const LOCK_HEARTBEAT_MS = 15 * 1000;

// Cloud Functions (same region as backend)

const FunnelIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.5 3.5a1 1 0 0 1 1-1h9a1 1 0 0 1 .8 1.6L9.5 9v3.4a.6.6 0 0 1-.32.53l-2 1A.6.6 0 0 1 6 13.8V9L2.7 4.1a1 1 0 0 1 .8-1.6Z"
      fill="currentColor"
    />
  </svg>
);

interface AuditEntry {
  action: AuditAction;
  by: string | null;
  at: string; // ISO
}

// Firestore-typer
type FirestoreDate = any;
type AuditAction = "created" | "updated" | "completed" | "reopened" | "delete_marked" | "delete_unmarked";





export interface BaseItem {
  orderNumber: string;
  manufacturer: string;
  model: string;
  productType?: string | null;
  warrantyStartDate?: string | null;

  // Serial (detta fält lagras på item)
  serial: string;              // final display-serial

  // 🔎 Härledda/externa (från serialIndex). Behåll dem som optional i typen för läsning i UI,
  // men skriv dem normalt inte till itInventory.
  serialBase?: string;         // derivat av serial (utan visit) “ läses/visas, ej ägt här
  serialVisit?: number;        // derivat (running number) “ läses/visas, ej ägt här

  // Länknyckel till serialIndex (denna kan du vilja lagra på item som "foreign key")
  serialBaseKey?: string;      // normaliserad basnyckel för koppling mot serialIndex

  // Övriga fält
  chargerIncluded: boolean;
  adapterYesNo?: "Yes" | "No" | "";   // legacy-stöd
  damageNotes: string;
  photos: PhotoURLMap;

  customer?: string;
  customerId?: string | null;
  articleNumber?: string;

  createdAt: FirestoreDate;
  createdBy: string | null;
  initials?: string | null;
  auditLog: AuditEntry[];

  completed: boolean;
  completedAt: FirestoreDate;
  completedBy: string | null;

  updatedAt?: FirestoreDate;

  lockedBy?: string | null;
  lockedAt?: string | null;

  productTypeId?: string | null;

  // Statusval vid färdigställning
  reuse?: boolean;
  resold?: boolean;
  scrap?: boolean;

  // Gradering A“E (tom sträng = ej vald)
  grade?: "A" | "B" | "C" | "D" | "E" | "";

  // Fakturering
  markedForInvoice?: boolean;
  invoiceReportId?: string | null;
  invoicedAt?: string | null;

  // Sorteringsflagga för status (0=open, 1=ready, 2=invoiced)
  stateSort?: number | null;

  // Vidareförsäljning
  salesBatchId?: string | null;
  salesBatchName?: string | null;
  salesKolliId?: string | null;
  salesKolliName?: string | null;
  salesCpu?: string | null;
  salesStorage?: string | null;
  salesRam?: string | null;

  // Delete-markering
  deletePending?: boolean;
  deleteMarkedBy?: string | null;
  deleteMarkedAt?: string | null;
}

export interface Item extends BaseItem {
  id: string;
}

interface FormState {
  orderNumber: string;
  manufacturer: string;
  model: string;
  productType: string;
  productTypeId?: string;
  warrantyStartDate: string;
  serial: string;
  chargerIncluded: boolean;
  adapterYesNo: string;

  customer: string;
  customerKey?: string;
  articleNumber: string;
  damageNotes: string;
  photos: ThumbnailMap; // dataURL för UI:t
  // nya fält
  reuse: boolean;
  resold: boolean;
  scrap: boolean;

  salesBatchId?: string;
  salesBatchName?: string;
  salesKolliId?: string;
  salesKolliName?: string;
  salesCpu?: string;
  salesStorage?: string;
  salesRam?: string;

}

interface Filters {
  orderNumber: string;
  manufacturer: string;
  model: string;
  customer: string;
  serial: string;
  chargerIncluded: string;
  createdAt: string;
  createdBy: string;
  [key: string]: string;
}

/* =========================
   Konstanter & små helpers
========================= */

// Dev: visa mina auth-claims i konsolen
 (window as any).showClaims = async () => {
  try {
    if (!auth.currentUser) {
      console.debug("Inte inloggad");
      return null;
    }
    const t = await auth.currentUser.getIdTokenResult(true);
    console.debug("claims:", t.claims);
    return t.claims;
  } catch (e) {
    console.error(e);
    return null;
  }
};
// === Shared store för original serial-info vid Edit (kan läsas/skrivas av App & EditModalBody) ===
type EditOriginalStore = {
  serial: string;
  serialBaseKey: string | null;
  itemId: string | null;
};

// OBS: inte React.useRef här (hooks får ej ligga utanför komponenter).
// Detta är en enkel, muterbar behållare som båda funktionerna kan dela.
const editOriginalRef: { current: EditOriginalStore } = {
  current: { serial: "", serialBaseKey: null, itemId: null }
};

// ✅ LÄGG IN DIREKT UNDER editOriginalRef (modulnivå, inte i en komponent)
type LastSerialAlloc = {
  changed: boolean;
  serialBaseKey: string;
  serialVisit: number;
  displaySerial: string;
};
const lastSerialAllocRef: { current: LastSerialAlloc | null } = { current: null };

export default function App(): JSX.Element {
  useDevTools();
  useEffect(() => { registerVendorDevHelpers(); }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const isCustomerPortalRef = useRef(false);
  // Sidor/meny
  const [activePage, setActivePageState] = useState<PageKey>(() => pathToPage(location.pathname));
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [reportsOpen, setReportsOpen] = useState(true);
  const [reportsView, setReportsView] = useState<"fakturor" | "klimat">(() =>
    reportsViewFromPath(location.pathname)
  );
  const [creatingReport, setCreatingReport] = useState(false);
  const [deleteClearedWhileOpen, setDeleteClearedWhileOpen] = useState(false);
  const [rpCustomerOptsState, setRpCustomerOptsState] = useState<{ key: string; name: string }[]>([]);
  const [selectedCustomerIdForList, setSelectedCustomerIdForList] = useState<string | null>(null);
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<string | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const effectivePageSize = showCustomerModal ? 2000 : PAGE_SIZE;

  const goToPage = useCallback(
    (page: PageKey, opts?: { skipNavigate?: boolean }) => {
      // Kundkonton: tillåt bara hem eller klimatrapport
      if (isCustomerPortalRef.current) {
        if (page === "rapporter") {
          if (reportsView !== "klimat") setReportsView("klimat");
        } else {
          page = "home";
        }
      }
      setActivePageState(page);
      if (opts?.skipNavigate) return;
      const targetPath = pageKeyToPath(page, { reportsView });
      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    },
    [navigate, location.pathname, reportsView]
  );

  useEffect(() => {
    const nextPage = pathToPage(location.pathname);
    if (nextPage !== activePage) {
      setActivePageState(nextPage);
    }
    if (nextPage === "rapporter") {
      const nextView = reportsViewFromPath(location.pathname);
      if (nextView !== reportsView) {
        setReportsView(nextView);
      }
    }
  }, [location.pathname, activePage, reportsView]);

  useEffect(() => {
    if (activePage !== "rapporter") return;
    const isReportDetail =
      location.pathname.startsWith("/rapporter/") && location.pathname !== "/rapporter/klimat";
    if (isReportDetail) return;
    const targetPath = pageKeyToPath("rapporter", { reportsView });
    if (location.pathname === targetPath) return;
    const replace = location.pathname.startsWith("/rapporter");
    navigate(targetPath, { replace });
  }, [activePage, reportsView, location.pathname, navigate]);

  // Wizard
  const quickOrderRef = React.useRef<HTMLInputElement | null>(null);
  const TOTAL_STEPS = 11;

  // Data & UI state
  const [isLoadingItems] = useState<boolean>(false);
  const userEditedManufacturerRef = React.useRef<boolean>(false);
  const [form, setForm] = useState<FormState>(() => createEmptyRegistrationForm());

  const {
    manufacturerList,
    newManufacturer,
    setNewManufacturer,
    showNewManufacturerInput,
    setShowNewManufacturerInput,
    modelList,
    newModel,
    setNewModel,
    showNewModelInput,
    setShowNewModelInput,
    newCustomer,
    setNewCustomer,
    showNewCustomerInput,
    setShowNewCustomerInput,
    customerListOpts,
    fetchCustomers,
    fetchManufacturers,
    fetchModelsFor,
    handleNewManufacturerAdd,
    handleNewModelAdd,
    handleNewCustomerAdd,
    manufacturerExists,
    modelExistsForThisManufacturer,
  } = useDirectoryLists({
    db,
    auth,
    form,
    setForm,
    rpCustomerOpts: rpCustomerOptsState,
    setRpCustomerOpts: setRpCustomerOptsState,
  });

  const {
    articleList,
    setArticleList,
    newArticle,
    setNewArticle,
    showNewArticleInput,
    setShowNewArticleInput,
    fetchArticlesFor,
    handleNewArticleAdd,
  } = useArticlesByCustomer({
    db,
    auth,
    form,
    setForm,
    customerListOpts,
  });

  const [thumbnailPreviews, setThumbnailPreviews] = useState<ThumbnailMap>({});
  const customerNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (Array.isArray(customerListOpts) ? customerListOpts : []).forEach((c) => { map[c.key] = c.name; });
    return map;
  }, [customerListOpts]);

  // --- Fakturering: UI-state ---
  const [billingCustomerFilter, setBillingCustomerFilter] = useState<string>(""); // "" = alla
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const [largeImage, setLargeImage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Mobil: visa/dölj filter-panel
  // Modeller per tillverkare
  const userEditedModelRef = React.useRef<boolean>(false); // stoppa auto-fyll när användaren editerat


  const { aiSuggestions, setAiSuggestions, requestSuggestions } = useAiSuggestions({
    suggestItemDetailsCallable,
  });

  // sentinel för auto-load (används av IntersectionObserver)
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);


  // Refs för att kunna läsa aktuell vy/sida inne i sentineln utan att resubscriba
  const activePageRef = useRef<PageKey>(activePage);
  const setItemsRef = useRef<React.Dispatch<React.SetStateAction<Item[]>> | null>(null);
  const fetchFirstPageRef = useRef<null | (() => Promise<void>)>(null);
  const fetchNextPageRef = useRef<null | (() => Promise<void>)>(null);
  const pageHasNextRef = useRef<boolean>(false);
  const pageIsLoadingRef = useRef<boolean>(false);
  const startHomeSentinelRef = useRef<null | (() => void)>(null);
  const stopHomeSentinelRef = useRef<null | (() => void)>(null);
  const stopLockHeartbeatRef = useRef<() => void>(() => { return; });
  const stopLockWatcherRef = useRef<() => void>(() => { return; });

  // Vem är inloggad? (för logg/metadata)
  const currentUserString = React.useCallback((): string | null => {
    return getCurrentUserString(auth);
  }, []); // <-- Tom dependency array är korrekt eftersom den bara beror på `auth` (som är stabilt)

  const {
    isEditOpen,
    setIsEditOpen,
    editId,
    setEditId,
    editIsReadOnly,
    setEditIsReadOnly,
    editInvoiceReportId,
    setEditInvoiceReportId,
    editForm,
    setEditForm,
    uploadProgress,
    setUploadProgress,
    closeEdit,
    handleEditChange,
  } = useEditItem({
    db,
    stopLockHeartbeat: stopLockHeartbeatRef,
    stopLockWatcher: stopLockWatcherRef,
  });
  const editIdRef = useRef<string | null>(null);
  useEffect(() => { editIdRef.current = editId; }, [editId]);
  const openedReadOnlyDueToDeleteRef = useRef<boolean>(false);

  const {
    draftItemId,
    setDraftItemId,
    currentStep,
    setCurrentStep,
    entryMode,
    setEntryMode,
    updateDraftMeta,
    ensureDraftItem,
  } = useDraftWizard({
    currentUserString,
    draftTtlMs: DRAFT_TTL_MS,
  });

  const {
    user,
    authReady,
    isCustomerPortal,
    customerKeys,
    setCustomerKeys,
  } = useAuthState<Item>({
    auth,
    fetchManufacturers,
    fetchCustomers,
    setItems: (updater) => {
      setItemsRef.current?.(updater);
    },
    fetchFirstPage: async () => {
      if (fetchFirstPageRef.current) await fetchFirstPageRef.current();
    },
    startHomeSentinel: () => startHomeSentinelRef.current?.(),
    stopHomeSentinel: () => stopHomeSentinelRef.current?.(),
    activePageRef,
    setActivePage: setActivePageState,
  });

  const {
    rpFrom,
    setRpFrom,
    rpTo,
    setRpTo,
    rpCustomerOpts,
    rpSelectedCustomers,
    setRpSelectedCustomers,
    rpSelectedTypes,
    setRpSelectedTypes,
    rpTypeOpts,
    setRpTypeOpts,
    showNewProductTypeInput,
    setShowNewProductTypeInput,
    newProductType,
    setNewProductType,
    productTypeExists,
    setRpTypesPrimed,
  } = useReportFilters({
    db,
    auth,
    user,
    authReady,
    isCustomerPortal,
    customerKeys,
    loadProductTypesForImpact,
    customerOptsState: [rpCustomerOptsState, setRpCustomerOptsState],
  });

  useEffect(() => {
    if (!isEditOpen) {
      openedReadOnlyDueToDeleteRef.current = false;
      setDeleteClearedWhileOpen(false);
    }
  }, [isEditOpen]);

  useEditLiveSync({
    db,
    editId,
    isEditOpen,
    currentUserString,
    isCustomerPortal,
    setEditForm,
    setEditIsReadOnly,
    openedReadOnlyDueToDeleteRef,
    setDeleteClearedWhileOpen,
  });

  const isCustomerScoped = isCustomer(user);
  const isCustomerPortalEffective = isCustomerScoped || isCustomerPortal;
  isCustomerPortalRef.current = isCustomerPortalEffective;

  // Gate manuella URL:er för kundkonton (tillåt bara hem och klimatrapport)
  useEffect(() => {
    if (!isCustomerPortalEffective) return;
    const nextPage = pathToPage(location.pathname);
    const nextView = reportsViewFromPath(location.pathname);
    const allowRapporterKlimat = nextPage === "rapporter" && nextView === "klimat";
    if (nextPage === "rapporter" && nextView !== "klimat") {
      setReportsView("klimat");
      navigate("/rapporter/klimat", { replace: true });
      return;
    }
    if (nextPage !== "home" && !allowRapporterKlimat) {
      if (activePage !== "home") setActivePageState("home");
      if (location.pathname !== "/") navigate("/", { replace: true });
    }
  }, [isCustomerPortalEffective, location.pathname, navigate, activePage]);

  const {
    filters,
    setFilters,
    filtersActive,
    queryFilters,
    createdAtRange,
    openFilterKey,
    setOpenFilterKey,
    filterSearch,
    setFilterSearch,
    loadingFilterOptions,
    filterOptions,
    distinctValues,
    filterAnchor,
    handleOpenFilter,
  } = useInventoryFilters<Filters>({
    db,
    customerNameById,
    isCustomerPortal: isCustomerPortalEffective,
    customerKeys,
    initialFilters: {
      orderNumber: "",
      manufacturer: "",
      model: "",
      customer: "",
      serial: "",
      chargerIncluded: "",
      createdAt: "",
      createdBy: "",
    },
  });

  const clearAllFilters = useCallback(() => {
    setFilters({
      orderNumber: "",
      manufacturer: "",
      model: "",
      customer: "",
      serial: "",
      chargerIncluded: "",
      createdAt: "",
      createdBy: "",
    });
    setOpenFilterKey(null);
  }, [setFilters, setOpenFilterKey]);

  const {
    items,
    setItems,
    pageHasNext,
    pageIndex,
    pageIsLoading,
    pageLastRefreshAt,
    hasNewTopItems,
    setHasNewTopItems,
    fetchFirstPage,
    fetchNextPage,
    startHomeSentinel,
    stopHomeSentinel,
    lastError,
  } = useInventoryList<Item>({
    auth,
    db,
    pageSize: effectivePageSize,
    isCustomerPortal: isCustomerPortalEffective,
    customerKeys,
    draftItemId,
    activePageRef,
    filters: queryFilters,
    createdAtRange,
    customerFilterOverride:
      activePage === "kunder" && selectedCustomerIdForList ? [selectedCustomerIdForList] : undefined,
  });


  // uppdatera refs för auth-hookens callbacks
  setItemsRef.current = setItems;
  fetchFirstPageRef.current = fetchFirstPage;
  fetchNextPageRef.current = fetchNextPage;
  pageHasNextRef.current = pageHasNext;
  pageIsLoadingRef.current = pageIsLoading;
  startHomeSentinelRef.current = startHomeSentinel;
  stopHomeSentinelRef.current = stopHomeSentinel;

  const resetRegistrationSession = useCallback(() => {
    setForm(createEmptyRegistrationForm());
    setThumbnailPreviews({});
    setUploadProgress({});
    setDraftItemId(null);
    setCurrentStep(1);
    setEntryMode("wizard");
    setAiSuggestions({});
    setIsSaving(false);
  }, [setAiSuggestions, setCurrentStep, setDraftItemId, setEntryMode, setUploadProgress]);

  const { MENU, handleLogout, refreshHomeList } = useAppShell({
    auth,
    user,
    activePage,
    setActivePage: goToPage,
    isCustomerPortal: isCustomerPortalEffective,
    filtersActive,
    authReady,
    fetchFirstPage,
    startHomeSentinel,
    stopHomeSentinel,
    onLoggedOut: resetRegistrationSession,
  });

  // Mobilbrytarstate (uppdateras vid resize)
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const {
    cards: customerCards,
    loading: customerCardsLoading,
    error: customerCardsError,
  } = useCustomerCards(db, {
    enabled: activePage === "kunder" && authReady && user?.status === "active"
      && (user.role === "admin" || user.role === "user"),
  });
  const {
    orderNumbers: customerOrderNumbers,
  } = useCustomerOrderNumbers(db, showCustomerModal ? selectedCustomerIdForList : null);

  useEffect(() => {
    if (activePage !== "kunder") return;
    if (!showCustomerModal) {
      setSelectedCustomerIdForList(null);
      setSelectedCustomerOrder(null);
    }
  }, [activePage, showCustomerModal]);

  useEffect(() => {
    if (activePage !== "kunder") return;
    if (!selectedCustomerIdForList) return;
    if (!showCustomerModal) return;
    fetchFirstPage();
  }, [activePage, selectedCustomerIdForList, showCustomerModal, fetchFirstPage]);

  useEffect(() => {
    if (activePage !== "kunder") return;
    setFilters((prev) => ({
      ...prev,
      orderNumber: selectedCustomerOrder || "",
    }));
  }, [activePage, selectedCustomerOrder, setFilters]);

  useEffect(() => {
    if (activePage !== "kunder") {
      setFilters({
        orderNumber: "",
        manufacturer: "",
        model: "",
        customer: "",
        serial: "",
        chargerIncluded: "",
        createdAt: "",
        createdBy: "",
      });
      setSelectedCustomerIdForList(null);
      setSelectedCustomerOrder(null);
      setShowCustomerModal(false);
    }
  }, [activePage, setFilters]);

  const reportIdFromHash = useReportHash();
  const { showLogoutNotice, setShowLogoutNotice } = useClaimsBumpNotice(authReady, user?.uid ?? null);
  useCustomerPortalGuard(
    isCustomerPortalEffective,
    customerKeys,
    customerListOpts,
    setCustomerKeys,
    activePageRef,
    goToPage
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // [NYTT] En enhetlig “refresh efter mutationer
  const {
    pendingDeletableIds,
    deleteModalItems,
    isDeleting,
    openDeleteModal,
    cancelDeleteModal,
    confirmDeleteModal,
    getDeleteToggleMeta,
  } = useDeleteActions({
    db,
    storage,
    items,
    currentUserString,
    setDeleteConfirmText,
    setShowDeleteModal,
    deleteConfirmText,
    refreshHomeList,
  });

  const { startLockHeartbeat, startLockWatcher } = useEditLocks({
    db,
    lockMs: LOCK_HEARTBEAT_MS,
    currentUserString,
    setEditIsReadOnly,
    setEditForm,
    isEditOpen,
    editIdRef,
    isCustomerPortal: isCustomerPortalEffective,
    stopLockHeartbeatRef,
    stopLockWatcherRef,
  });


  const {
    nextStep,
    prevStep,
    stepValid,
    StepIndicator,
  } = useWizardNavigation({
    currentStep,
    setCurrentStep,
    totalSteps: TOTAL_STEPS,
    form,
    setForm,
    ensureDraftItem,
    draftItemId,
    updateDraftMeta,
    aiSuggestions,
    setAiSuggestions,
    manufacturerList,
    modelList,
    rpTypeOpts,
    setShowNewModelInput,
    setNewModel,
    showNewModelInput,
    newModel,
    userEditedModelRef,
    userEditedManufacturerRef,
    fetchModelsFor,
    suggestItemDetailsCallable,
  });

  // --- Fakturering: härleda listor ---
  const {
    billingFilteredItems,
    allFilteredMarked,
    toggleMarkAllInFiltered,
  } = useBillingList<Item>({
    items,
    customerListOpts,
    billingCustomerFilter,
    setItems: (updater) => setItems(updater),
    setMarkedForInvoice: async (...args) => {
      if (setMarkedForInvoiceRef.current) {
        await setMarkedForInvoiceRef.current(...args);
      }
    },
    setIsMarkingAll,
  });

  // Hämta artiklar för vald kund (ID först, fallback på namn) — utan Samhall-defaults
  // Sammanlagd procent från alla bild-uploads
  const overallProgress = computeOverallProgress(uploadProgress);

  const { handleInputChange, handleSave } = useCreateItemFlow({
    db,
    storage,
    form,
    setForm,
    draftItemId,
    ensureDraftItem,
    aiSuggestions,
    rpCustomerOpts,
    splitSerialParts,
    normalizeSerialKey,
    setUploadProgress,
    currentUserString,
    refreshHomeList,
    setThumbnailPreviews,
    setDraftItemId,
    setCurrentStep,
    setIsSaving,
    isSaving,
    ensureProductTypeInDb,
  });







  const handlePhotoChange = createHandlePhotoChange({
    setForm,
    setThumbnailPreviews,
  });

  const handleRemovePhoto = createHandleRemovePhoto({
    setForm,
    setThumbnailPreviews,
  });

  const { openWarranty, markNoWarranty } = createWarrantyActions({
    setForm,
    callVendorLookup,
    buildWarrantyLink,
    copyToClipboard,
    focusSelector: 'input[name="warrantyStartDate"]',
  });







  /* Delete */





  // === openEdit (ersätt hela funktionen) ===
  const openEdit = useOpenEdit({
    db,
    isCustomerPortal: isCustomerPortalEffective,
    currentUserString,
    startLockHeartbeat,
    startLockWatcher,
    setEditIsReadOnly,
    setEditInvoiceReportId,
    setEditId,
    setEditForm,
    setIsEditOpen,
    editOriginalRef,
    lastSerialAllocRef,
    toMillis,
    LOCK_STALE_MS,
    openedReadOnlyDueToDeleteRef,
    setDeleteClearedWhileOpen,
  });
  const openItemById = React.useCallback((id: string) => {
    if (!id) return;
    void openEdit({ id } as any);
  }, [openEdit]);

  type LegacyMatch = import("./services/legacyCertClient").LegacyMatch;
  type CertificateResult = {
    blanccoHits: number;
    certusHits: number;
    legacyHits: number;
    matches: import("./components/LegacyCertList").CertificateMatch[];
    warnings?: string[];
  };

  const [certificateResult, setCertificateResult] = useState<CertificateResult | null>(null);

  const handleFetchCertificate = React.useCallback(async () => {
    setCertificateResult(null);
    const uuid = (editForm as any).blanccoReportUuid;
    const assetId = (editForm as any).assetId;
    const serialRaw = (editForm as any).serial as string | undefined;
    if (assetId === undefined && !uuid && !serialRaw) {
      alert("Ingen UUID, assetId eller serienummer att söka på.");
      return;
    }

    try {
      const { searchCertificates } = await import("./services/certificateSearch");
      return await searchCertificates({
        itemId: editId,
        serial: serialRaw,
        assetId,
        blanccoReportUuid: uuid,
        legacyLimit: 500,
      });
    } catch (e: any) {
      const msg = e?.message || "Kunde inte hämta certifikat.";
      alert(msg);
      return;
    }
  }, [editForm, editId]);

const { onToggleDeleteFromList, deleteTogglePendingIds } = useToggleDeleteFromList<Item>({
  db,
  toMillis,
  LOCK_STALE_MS,
  currentUserString,
  setItems,
});

  const saveEdit = useSaveEdit({
    db,
    editId,
    editForm,
    editIsReadOnly,
    currentUserString,
    toMillis,
    LOCK_STALE_MS,
    refreshHomeList,
    setIsEditOpen,
    setEditId,
    setEditForm,
  });

  const { setMarkedForInvoice } = useInvoiceToggle({
    db,
    currentUserString,
    toMillis,
    LOCK_STALE_MS,
  });
  const setMarkedForInvoiceRef = useRef<(id: string, checked: boolean) => Promise<void> | void>();
  setMarkedForInvoiceRef.current = setMarkedForInvoice;



  const markAsCompleted = useEditCompletion({
    db,
    editId,
    editForm,
    currentUserString,
    toMillis,
    LOCK_STALE_MS,
    splitSerialParts,
    normalizeSerialKey,
    buildDisplaySerial,
    lastSerialAllocRef,
    refreshHomeList,
    setEditForm,
    setIsEditOpen,
    setEditId,
  }).markAsCompleted;




  const reopenForEditing = useEditReopen({
    db,
    editId,
    currentUserString,
    toMillis,
    LOCK_STALE_MS,
    refreshHomeList,
    setEditForm,
  }).reopenForEditing;

  const unmarkDelete = async (): Promise<void> => {
    if (!editId) return;
    try {
      setIsSaving(true);
      const nowIso = new Date().toISOString();

      await updateDoc(doc(db, "itInventory", editId), {
        deletePending: false,
        deleteMarkedBy: null,
        deleteMarkedAt: null,
        auditLog: arrayUnion({
          action: "delete_unmarked",
          by: currentUserString(),
          at: nowIso
        })
      } as UpdateData<BaseItem>);

      // Uppdatera lokalt UI-state direkt
      setEditForm(prev => ({
        ...prev,
        deletePending: false,
        deleteMarkedBy: null,
        deleteMarkedAt: null,
        auditLog: [
          ...(Array.isArray(prev.auditLog) ? prev.auditLog : []),
          { action: "delete_unmarked", by: currentUserString(), at: nowIso }
        ]
      }));
    } catch (e: any) {
      alert("Kunde inte avmarkera radering: " + (e?.message || e));
    } finally {
      setIsSaving(false);
    }
  };


  const visibleItems = useVisibleItems(
    items,
    filters,
    customerNameById,
    activePage === "kunder" ? selectedCustomerIdForList : null,
    currentUserString()
  );


  /* Stegindikator */
  const { handleIsoDateInput } = useIsoDateMask((val) => {
    setForm((p) => ({ ...p, warrantyStartDate: val }));
  });

  //==================== 2 useMemo, useCallback och const funktioner ovanför============================================//


  // --- Admin-gated debug (?debug=1) ---
  const [debugAllowed, setDebugAllowed] = useState(false);
  const DEBUG = useDebugGate(user, setDebugAllowed);

  // Mount force-logout watcher for any signed-in user so claims changes are always caught,
  // even while the account is still "unassigned" or pending verification.
  const forceLogoutEnabled = authReady && !!user;
  useForceLogoutOnClaimsBump(forceLogoutEnabled);


  dbg("[forceLogout] enabled?", {
    enabled: forceLogoutEnabled,
    uid: user?.uid,
    email: user?.email,
  });


  const isReportView = !!reportIdFromHash;

  useEffect(() => {
    if (isCustomer(user) && activePage === "fakturering") {
      goToPage("home");
      activePageRef.current = "home";
    }
  }, [user, activePage, goToPage]);

  // Starta/stäng sentinel baserat på vy & auth (HOOK PÅ TOPPNIVÅ)
  useEffect(() => {
    if (!authReady) return;

    if (activePage !== "home") {
      // lämnat Hem - stäng lyssnaren
      stopHomeSentinel();
      return;
    }
    if (filtersActive) {
      // vid filter kör vi explicit fetch och stänger sentinel för att undvika ofiltrerade toppnotiser
      stopHomeSentinel();
      return;
    }

    // på Hem — starta lyssnaren
    startHomeSentinel();
    return () => stopHomeSentinel();
  }, [authReady, activePage, filtersActive, startHomeSentinel, stopHomeSentinel]);

  useEffect(() => { activePageRef.current = activePage; }, [activePage]);



  // Ladda första sidan av Hem-listan när auth är klart och Hem-fliken är aktiv
  useEffect(() => {
    if (!authReady) return;
    if (activePage !== "home") return;
    fetchFirstPage();
  }, [authReady, activePage, fetchFirstPage, queryFilters]);

  // 🛠️ Exponera refresh-funktion globalt för Rapporter-vyn
  useExposeFetchFirstPage(fetchFirstPage);

  // Ladda ID-baserade kundalternativ när Users-sidan öppnas (eller om listan är tom)
  useEffect(() => {
    if (activePage !== "users") return;
    // Om vi redan har laddat, gör inget
    if (Array.isArray(customerListOpts) && customerListOpts.length > 0) return;

    (async () => {
      try {
        await fetchCustomers(); // din uppdaterade fetchCustomers fyller customerListOpts
      } catch (e) {
        console.warn("Kunde inte ladda customers för Users-sidan:", e);
      }
    })();
  }, [activePage, customerListOpts, fetchCustomers]);

  // Nollställ faktureringsfiltret om valt värde inte finns bland kund-IDs
  useEffect(() => {
    if (!billingCustomerFilter) return;
    const exists = customerListOpts.some(o => o.key === billingCustomerFilter);
    if (!exists) {
      setBillingCustomerFilter(""); // fallback till "Alla kunder"
    }
  }, [billingCustomerFilter, customerListOpts]);

  useEffect(() => {
    if (!isEditOpen) {
      stopLockHeartbeatRef.current?.();
      stopLockWatcherRef.current?.();
    }
  }, [isEditOpen]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);


  useEffect(() => {
    // Om man råkar hamna efter steg 2 utan vald kund: tvinga tillbaka till steg 2
    if (activePage === "home" && currentStep > 2 && !form.customer.trim()) {
      setCurrentStep(2);
    }
  }, [activePage, currentStep, form.customer, setCurrentStep]);
  // Apply AI suggestions when entering steps 4-6 (idempotent)
  useEffect(() => {
    if (currentStep >= 4 && aiSuggestions?.manufacturer && !form.manufacturer) {
      if (userEditedManufacturerRef.current) return; // respektera användarens editering
      setForm((p) => ({ ...p, manufacturer: aiSuggestions.manufacturer! }));
    }
  }, [aiSuggestions?.manufacturer, currentStep, form.manufacturer, setForm]);

  useEffect(() => {
    if (currentStep >= 5 && aiSuggestions?.model && !form.model) {
      setForm((p) => ({ ...p, model: aiSuggestions.model! }));
    }
  }, [aiSuggestions?.model, currentStep, form.model, setForm]);

  useEffect(() => {
    if (currentStep >= 6 && aiSuggestions?.productType && !form.productType) {
      setForm((p) => ({
        ...p,
        ...resolveProductTypeSelection(rpTypeOpts, aiSuggestions.productType!),
      }));
    }
  }, [aiSuggestions?.productType, currentStep, form.productType, rpTypeOpts, setForm]);


  // If AI suggested a model not in DB at step 5, open "new model" input and prefill
  useEffect(() => {
    if (currentStep === 5 && aiSuggestions?.model) {
      if (userEditedModelRef.current) return; // respektera användarens editering
      const exists = modelList.some((m) => toKey(m) === toKey(aiSuggestions.model!));
      if (!exists) {
        setShowNewModelInput(true);
        setNewModel(aiSuggestions.model!);
        try { console.debug('[AI:prefill-model] set', { aiModel: aiSuggestions.model }); } catch (e) { void e; }
      }
    }
  }, [aiSuggestions?.model, currentStep, modelList, setNewModel, setShowNewModelInput]);


  useEffect(() => {
    fetchModelsFor(form.manufacturer);
    // Behåll AI-vald modell när tillverkare ändras i samma flöde
    if (!aiSuggestions?.model) {
      setForm((prev) => ({ ...prev, model: "" }));
    }
    if (!aiSuggestions?.model) setShowNewModelInput(false);
    if (!aiSuggestions?.model) setNewModel("");
    // Ny kontext: nulställ touched-flagga så ev. auto-fyll kan ske igen om användaren inte editerat
    userEditedModelRef.current = false;
  }, [aiSuggestions?.model, fetchModelsFor, form.manufacturer, setForm, setNewModel, setShowNewModelInput]);
  // Safety net: if new-model input is open but empty, prefill with AI model
  useEffect(() => {
    if (currentStep === 5 && showNewModelInput && (!newModel || !newModel.trim()) && aiSuggestions?.model) {
      if (userEditedModelRef.current) return; // användaren har rensat medvetet
      try { console.debug('[AI:prefill-model] safety', { aiModel: aiSuggestions.model }); } catch (e) { void e; }
      setNewModel(aiSuggestions.model);
    }
  }, [aiSuggestions?.model, currentStep, newModel, setNewModel, showNewModelInput]);

  // Render-time debug for AI notices
  useEffect(() => {
    if (currentStep === 4) {
      try { console.debug('[AI:render] step4', { manu: aiSuggestions?.manufacturer, has: !!aiSuggestions?.manufacturer }); } catch (e) { void e; }
    }
  }, [currentStep, aiSuggestions?.manufacturer]);
  useEffect(() => {
    if (currentStep === 5) {
      try { console.debug('[AI:render] step5', { showNewModelInput, newModel, aiModel: aiSuggestions?.model }); } catch (e) { void e; }
    }
  }, [currentStep, showNewModelInput, newModel, aiSuggestions?.model]);
  useEffect(() => {
    if (currentStep === 6) {
      try { console.debug('[AI:render] step6', { pt: aiSuggestions?.productType, has: !!aiSuggestions?.productType }); } catch (e) { void e; }
    }
  }, [currentStep, aiSuggestions?.productType]);

  // Ladda artiklar när vald kund (ID) ändras
  useEffect(() => {
    if (form.customerKey) {
      fetchArticlesFor(form.customerKey); // ✅ ID-baserat
    } else {
      setArticleList([]);
    }
  }, [fetchArticlesFor, form.customerKey, setArticleList]);



  // MENU BUTTON (öppna menyn)
  const MENU_BUTTON = (
    <button
      className="gw-menu-btn"
      aria-label="Öppna meny"
      aria-expanded={menuOpen}
      aria-controls="gw-side-menu"
      onClick={() => setMenuOpen(true)}
    >
      ☰
    </button>
  );

  // SIDE MENU (overlay + panel via portal)
  const SIDE_MENU_ALWAYS = createPortal(
    <SideMenu
      expanded={menuOpen}
      toggleExpanded={() => setMenuOpen((prev) => !prev)}
      menu={MENU}
      reportsOpen={reportsOpen}
      setReportsOpen={setReportsOpen}
      activePage={activePage}
      reportsView={reportsView}
      setReportsView={setReportsView}
      setActivePage={goToPage}
      setMenuOpen={setMenuOpen}
      isCustomerPortal={isCustomerPortalEffective}
      startHomeSentinel={startHomeSentinel}
      stopHomeSentinel={stopHomeSentinel}
      fetchFirstPage={fetchFirstPage}
      onLogout={handleLogout}
      showLogout={!!user}
    />,
    document.body
  );


  // ===== Auth Gate =====
  const CAN_DEBUG = DEBUG && debugAllowed;

  const showEntryToggle = activePage === "home" && !isCustomerPortalEffective;
  const canCreateTest = !isCustomerPortalEffective;

  const TOP_BAR = (
    <TopBarActions
      email={user?.email || null}
      onCreateTest={
        canCreateTest ? () => createTestItemViaPrompt({ refreshList: fetchFirstPage }) : undefined
      }
      showEntryToggle={showEntryToggle}
      entryMode={entryMode}
      setEntryMode={setEntryMode}
    />
  );

  const inventoryListNode = (
    <InventoryListView
      isLoadingItems={isLoadingItems}
      isMobile={isMobile}
      items={items}
      visibleItems={visibleItems}
      filters={filters}
      setFilters={setFilters}
      filtersActive={filtersActive}
      clearAllFilters={clearAllFilters}
      openFilterKey={openFilterKey}
      setOpenFilterKey={setOpenFilterKey}
      filterSearch={filterSearch}
      setFilterSearch={setFilterSearch}
      loadingFilterOptions={loadingFilterOptions}
      filterOptions={filterOptions}
      distinctValues={distinctValues}
      filterAnchor={filterAnchor}
      handleOpenFilter={handleOpenFilter}
      pendingDeletableIds={pendingDeletableIds}
      openDeleteModal={openDeleteModal}
      getDeleteToggleMeta={getDeleteToggleMeta}
      onToggleDeleteFromList={onToggleDeleteFromList}
      deleteTogglePendingIds={deleteTogglePendingIds}
      customerNameById={customerNameById}
      currentUserString={currentUserString}
      hasNewTopItems={hasNewTopItems}
      setHasNewTopItems={setHasNewTopItems}
      fetchFirstPage={fetchFirstPage}
      fetchNextPage={fetchNextPage}
      pageIndex={pageIndex}
      pageIsLoading={pageIsLoading}
      pageHasNext={pageHasNext}
      pageLastRefreshAt={pageLastRefreshAt}
      isCustomerPortal={isCustomerPortalEffective}
      lastError={lastError}
      FunnelIcon={FunnelIcon}
      openEdit={openEdit}
      showTableFilters={!showCustomerModal}
      tableWrapClassName={showCustomerModal ? "gw-table-wrap--in-modal gw-table-wrap--modal-tight h-full" : ""}
    />
  );

  const selectedCustomerCard = selectedCustomerIdForList
    ? customerCards.find((c) => c.id === selectedCustomerIdForList) || null
    : null;

  const CUSTOMER_DETAIL_MODAL = showCustomerModal && selectedCustomerIdForList ? createPortal(
    <div className="fixed inset-0 bg-black/50 z-[1200] grid place-items-center p-4">
      <div className="gw-card bg-surface p-0 w-full max-w-[1800px] h-[calc(100vh-64px)] overflow-hidden rounded-xl shadow-card flex flex-col min-h-0">
        <div className="flex items-center gap-3 border-b border-border px-4 pt-4 pb-3">
          <div className="flex flex-col">
            <div className="gw-h3 m-0">{selectedCustomerCard?.name || "Kund"}</div>
            <div className="text-sm text-muted">
              Enheter: {selectedCustomerCard?.total ?? "–"} · Klarmarkerade: {selectedCustomerCard?.completed ?? "–"}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <label className="text-sm text-muted">Ordernummer</label>
            <select
              className="gw-select w-[220px]"
              value={selectedCustomerOrder || ""}
              onChange={(e) => setSelectedCustomerOrder(e.target.value || null)}
            >
              <option value="">Visa alla</option>
              {customerOrderNumbers.map((ord) => (
                <option key={ord} value={ord}>{ord}</option>
              ))}
            </select>
            <button className="gw-btn gw-btn-secondary" onClick={() => { setShowCustomerModal(false); setSelectedCustomerOrder(null); }}>
              Stäng
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto w-full min-h-0 px-0">
          {inventoryListNode}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <AuthGate authReady={authReady} user={user} canDebug={CAN_DEBUG}>
    <div className={`goldwasser-app ${menuOpen ? "menu-is-open" : ""}`}>
        <ReportViewRouter isReportView={isReportView} reportIdFromHash={reportIdFromHash || null} authReady={authReady}>
          <AppShellLayout menuButton={MENU_BUTTON} sideMenu={SIDE_MENU_ALWAYS} topBar={TOP_BAR}>

              {/* ADMIN: Produkttyper */}
              {activePage === "productTypesAdmin" && <ProductTypesAdmin />}

              {/* Raderingscertifikat */}
              <CertificatesPage active={activePage === "certificates"} />

              <ReportsView
                isReportView={isReportView}
                reportIdFromHash={reportIdFromHash || null}
                authReady={authReady}
                activePage={activePage}
                reportsView={reportsView}
                rpFrom={rpFrom}
                rpTo={rpTo}
                rpCustomerOpts={rpCustomerOpts}
                rpSelectedCustomers={rpSelectedCustomers}
                setRpSelectedCustomers={setRpSelectedCustomers}
                rpSelectedTypes={rpSelectedTypes}
                setRpSelectedTypes={setRpSelectedTypes}
                rpTypeOpts={rpTypeOpts}
                setRpFrom={setRpFrom}
                setRpTo={setRpTo}
              />

              <VidareforsaljningView
                activePage={activePage}
                db={db}
                user={user}
                authReady={authReady}
                isCustomerPortal={isCustomerPortalEffective}
                onOpenItem={openItemById}
              />

              <CustomersPage
                active={activePage === "kunder" && !isCustomerPortalEffective}
                mode="index"
                cards={customerCards}
                loading={customerCardsLoading}
                error={customerCardsError}
                selectedCustomerId={selectedCustomerIdForList}
                onSelectCustomer={(id) => {
                  setSelectedCustomerIdForList(id);
                  setSelectedCustomerOrder(null);
                  setShowCustomerModal(true);
                }}
              />

              {/* HOME */}
              {activePage === "home" && (
                <>
                  {(() => { console.debug("[render] HOME gate", { activePage, isCustomerPortal: isCustomerPortalEffective }); return null; })()}

                  <HomeShell entryMode={entryMode} setEntryMode={setEntryMode} isCustomerPortal={isCustomerPortalEffective}>
                    {/* --- WIZARD-LÄGE --- */}
                    {entryMode === "wizard" && (
                      <HomeWizardView
                        StepIndicator={StepIndicator}
                        currentStep={currentStep}
                        form={form}
                        setForm={setForm}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        stepValid={stepValid}
                        handleInputChange={handleInputChange}
                        customerListOpts={customerListOpts}
                        showNewCustomerInput={showNewCustomerInput}
                        setShowNewCustomerInput={setShowNewCustomerInput}
                        newCustomer={newCustomer}
                        setNewCustomer={setNewCustomer}
                        handleNewCustomerAdd={handleNewCustomerAdd}
                        manufacturerList={manufacturerList}
                        aiSuggestions={aiSuggestions}
                        userEditedManufacturerRef={userEditedManufacturerRef}
                        userEditedModelRef={userEditedModelRef}
                        setShowNewManufacturerInput={setShowNewManufacturerInput}
                        showNewManufacturerInput={showNewManufacturerInput}
                        setNewManufacturer={setNewManufacturer}
                        newManufacturer={newManufacturer}
                        manufacturerExists={manufacturerExists}
                        handleNewManufacturerAdd={handleNewManufacturerAdd}
                        modelList={modelList}
                        showNewModelInput={showNewModelInput}
                        setShowNewModelInput={setShowNewModelInput}
                        newModel={newModel}
                        setNewModel={setNewModel}
                        modelExistsForThisManufacturer={modelExistsForThisManufacturer}
                        handleNewModelAdd={handleNewModelAdd}
                        rpTypeOpts={rpTypeOpts}
                        setShowNewProductTypeInput={setShowNewProductTypeInput}
                        showNewProductTypeInput={showNewProductTypeInput}
                        newProductType={newProductType}
                        setNewProductType={setNewProductType}
                        productTypeExists={productTypeExists}
                        ensureProductTypeInDb={ensureProductTypeInDb}
                        setRpTypeOpts={setRpTypeOpts}
                        setRpTypesPrimed={setRpTypesPrimed}
                        normalizeSerialKey={normalizeSerialKey}
                        splitSerialParts={splitSerialParts}
                        handleIsoDateInput={handleIsoDateInput}
                        markNoWarranty={markNoWarranty}
                        openWarranty={openWarranty}
                        thumbnailPreviews={thumbnailPreviews}
                        handlePhotoChange={handlePhotoChange}
                        handleRemovePhoto={handleRemovePhoto}
                        handleSave={handleSave}
                        isSaving={isSaving}
                        showNewArticleInput={showNewArticleInput}
                        setShowNewArticleInput={setShowNewArticleInput}
                        newArticle={newArticle}
                        setNewArticle={setNewArticle}
                        handleNewArticleAdd={handleNewArticleAdd}
                        articleList={articleList}
                        formatSerialForDisplay={formatSerialForDisplay}
                        fmtDateOnly={fmtDateOnly}
                        overallProgress={overallProgress}
                      />
                    )}

                    {/* --- SNABBINMATNING --- */}
                    {entryMode === "snabb" && (
                      <HomeQuickView
                        form={form}
                        setForm={setForm}
                        handleInputChange={handleInputChange}
                        customerListOpts={customerListOpts}
                        showNewCustomerInput={showNewCustomerInput}
                        setShowNewCustomerInput={setShowNewCustomerInput}
                        newCustomer={newCustomer}
                        setNewCustomer={setNewCustomer}
                        handleNewCustomerAdd={handleNewCustomerAdd}
                        aiSuggestions={aiSuggestions}
                        requestSuggestions={requestSuggestions}
                        rpTypeOpts={rpTypeOpts}
                        manufacturerList={manufacturerList}
                        modelList={modelList}
                        setShowNewManufacturerInput={setShowNewManufacturerInput}
                        showNewManufacturerInput={showNewManufacturerInput}
                        newManufacturer={newManufacturer}
                        setNewManufacturer={setNewManufacturer}
                        manufacturerExists={manufacturerExists}
                        handleNewManufacturerAdd={handleNewManufacturerAdd}
                        setShowNewModelInput={setShowNewModelInput}
                        showNewModelInput={showNewModelInput}
                        newModel={newModel}
                        setNewModel={setNewModel}
                        userEditedModelRef={userEditedModelRef}
                        modelExistsForThisManufacturer={modelExistsForThisManufacturer}
                        handleNewModelAdd={handleNewModelAdd}
                        setShowNewProductTypeInput={setShowNewProductTypeInput}
                        showNewProductTypeInput={showNewProductTypeInput}
                        newProductType={newProductType}
                        setNewProductType={setNewProductType}
                        productTypeExists={productTypeExists}
                        ensureProductTypeInDb={ensureProductTypeInDb}
                        setRpTypeOpts={setRpTypeOpts}
                        setRpTypesPrimed={setRpTypesPrimed}
                        articleList={articleList}
                        showNewArticleInput={showNewArticleInput}
                        setShowNewArticleInput={setShowNewArticleInput}
                        newArticle={newArticle}
                        setNewArticle={setNewArticle}
                        handleNewArticleAdd={handleNewArticleAdd}
                        normalizeSerialKey={normalizeSerialKey}
                        splitSerialParts={splitSerialParts}
                        openWarranty={openWarranty}
                        markNoWarranty={markNoWarranty}
                        handleIsoDateInput={handleIsoDateInput}
                        parseLooseDateToISO={parseLooseDateToISO}
                        handlePhotoChange={handlePhotoChange}
                        handleRemovePhoto={handleRemovePhoto}
                        thumbnailPreviews={thumbnailPreviews}
                        handleSave={handleSave}
                        isSaving={isSaving}
                        quickOrderRef={quickOrderRef}
                        formatSerialForDisplay={formatSerialForDisplay}
                        fmtDateOnly={fmtDateOnly}
                      />
                    )}
                  </HomeShell>

                  {/* Lista + edit-modal */}
                  <InventoryListView
                    isLoadingItems={isLoadingItems}
                    isMobile={isMobile}
                    items={items}
                    visibleItems={visibleItems}
                    filters={filters}
                    setFilters={setFilters}
                    filtersActive={filtersActive}
                    clearAllFilters={clearAllFilters}
                    openFilterKey={openFilterKey}
                    setOpenFilterKey={setOpenFilterKey}
                    filterSearch={filterSearch}
                    setFilterSearch={setFilterSearch}
                    loadingFilterOptions={loadingFilterOptions}
                    filterOptions={filterOptions}
                    distinctValues={distinctValues}
                    filterAnchor={filterAnchor}
                    handleOpenFilter={handleOpenFilter}
                    pendingDeletableIds={pendingDeletableIds}
                    openDeleteModal={openDeleteModal}
                    getDeleteToggleMeta={getDeleteToggleMeta}
                    onToggleDeleteFromList={onToggleDeleteFromList}
                    deleteTogglePendingIds={deleteTogglePendingIds}
                    customerNameById={customerNameById}
                    currentUserString={currentUserString}
                    hasNewTopItems={hasNewTopItems}
                    setHasNewTopItems={setHasNewTopItems}
                    fetchFirstPage={fetchFirstPage}
                    fetchNextPage={fetchNextPage}
                    pageIndex={pageIndex}
                    pageIsLoading={pageIsLoading}
                    pageHasNext={pageHasNext}
                    pageLastRefreshAt={pageLastRefreshAt}
                    loadMoreRef={loadMoreRef}
                    isCustomerPortal={isCustomerPortalEffective}
                    lastError={lastError}
                    FunnelIcon={FunnelIcon}
                    openEdit={openEdit}
                  />

                </>
              )}

              {/* Övriga sidor */}
              {activePage === "fakturering" && (() => { console.debug("[render] FAKTURERING gate", { activePage }); return null; })()}

              {
                
                activePage === "fakturering" && (
                  <InvoicingPage
                    user={user}
                    isCustomer={isCustomer}
                    billingCustomerFilter={billingCustomerFilter}
                    setBillingCustomerFilter={setBillingCustomerFilter}
                    billingFilteredItems={billingFilteredItems}
                    allFilteredMarked={allFilteredMarked}
                    isMarkingAll={isMarkingAll}
                    creatingReport={creatingReport}
                    setCreatingReport={setCreatingReport}
                    toggleMarkAllInFiltered={toggleMarkAllInFiltered}
                    setMarkedForInvoice={setMarkedForInvoice}
                    updateItemsState={(updater) => setItems(prev => updater(prev as any) as any)}
                    customerListOpts={customerListOpts}
                    computeBillingSteps={computeBillingSteps}
                    fmtDateOnly={fmtDateOnly}
                    formatSerialForDisplay={formatSerialForDisplay}
                    toEpochMillis={toEpochMillis}
                    createInvoiceReportCF={createInvoiceReportCF}
                    createInvoiceReportLocal={async () =>
                      generateInvoiceReportForMarkedItems(
                        (items as any[]).filter((it: any) => it?.completed === true),
                        user?.email ?? null
                      )
                    }
                    fetchFirstPage={fetchFirstPage}
                  />
                )
              }

              {/* ANVÄNDARE (endast admin) */}
              {activePage === "users" && user?.role === "admin" && <UserAdmin />}

              {/* BUG BOARD (endast admin) */}
              {activePage === "bugBoard" && user?.role === "admin" && (
                <Suspense
                  fallback={
                    <div className="gw-card p-4 text-muted">
                      Laddar Bug Board…
                    </div>
                  }
                >
                  <BugBoard />
                </Suspense>
              )}

          </AppShellLayout>
        </ReportViewRouter>

        {/* Edit-modal + Delete-modal (global portals) */}
        {createPortal(
          <EditModal
            isOpen={isEditOpen}
            onClose={closeEdit}
            manufacturerList={manufacturerList}
            productTypeOptions={rpTypeOpts}
            editForm={editForm}
            onChange={handleEditChange}
            onSave={saveEdit}
            largeImage={largeImage}
            setLargeImage={setLargeImage}
            onComplete={markAsCompleted}
            onReopen={reopenForEditing}
            isCompleted={!!editForm.completed}
            isReadOnly={isCustomerPortalEffective || editIsReadOnly}
            invoiceReportId={editInvoiceReportId}
            isSaving={isSaving}
            isCustomerAccount={isCustomerPortalEffective}
            onUnmarkDelete={unmarkDelete}
            setEditForm={setEditForm}
            itemId={editId!}
            deleteClearedWhileOpen={deleteClearedWhileOpen}
            editOriginalRef={editOriginalRef}
            lastSerialAllocRef={lastSerialAllocRef}
            setIsSaving={setIsSaving}
            refreshHomeList={refreshHomeList}
            setIsEditOpen={setIsEditOpen}
            setEditId={setEditId}
            onFetchCertificate={async () => {
              const res = await handleFetchCertificate();
              if (res) {
                setCertificateResult(res);
              }
              return res;
            }}
            currentUserString={currentUserString}
          />,
          document.body
        )}
        {certificateResult &&
          createPortal(
            <CertificateResultModal
              result={certificateResult}
              onClose={() => setCertificateResult(null)}
            />,
            document.body
          )}

        {showDeleteModal && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-title"
            className="gw-modal-overlay"
          >
            <div className="gw-modal-card max-w-[520px] p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <h3 id="delete-title" className="gw-modal-title m-0">Bekräfta radering</h3>
                <button
                  type="button"
                  onClick={cancelDeleteModal}
                  className="gw-btn gw-btn-ghost gw-btn-icon"
                  aria-label="Stäng dialog"
                >
                  ×
                </button>
              </div>

              <p className="text-muted mt-3 mb-3">
                Du är på väg att radera <b>{deleteModalItems.length}</b> markerad(e) enhet(er).
                Skriv <code>DELETE</code> med stora bokstäver för att bekräfta.
              </p>

              <ul className="max-h-48 overflow-y-auto border border-border rounded-md mb-4 divide-y divide-border">
                {deleteModalItems.map((item) => (
                  <li key={item.id} className="px-3 py-2 text-sm">
                    <div><strong>{item.orderNumber || "Order saknas"}</strong></div>
                    <div className="text-muted">Serienummer: {item.serial || "saknas"}</div>
                    <div className="text-xs text-muted break-all">Item-ID: {item.id}</div>
                  </li>
                ))}
              </ul>

              <label className="gw-form-field mb-4">
                <span className="gw-form-label">Bekräftelse</span>
                <input
                  type="text"
                  autoFocus
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  disabled={isDeleting}
                  placeholder='Skriv "DELETE"'
                  aria-label='Skriv "DELETE" för att bekräfta'
                  className="gw-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && deleteConfirmText === "DELETE") {
                      void confirmDeleteModal();
                    }
                  }}
                />
              </label>

              {deleteConfirmText !== "DELETE" && (
                <div className="text-xs text-muted mb-3">
                  Skriv exakt DELETE för att aktivera radera-knappen.
                </div>
              )}

              <div className="gw-modal-actions">
                <button onClick={cancelDeleteModal} disabled={isDeleting} className="gw-btn gw-btn-secondary">
                  Avbryt
                </button>
                <button
                  onClick={confirmDeleteModal}
                  disabled={deleteConfirmText !== "DELETE" || isDeleting}
                  className="gw-btn gw-btn-danger"
                  title={deleteConfirmText === "DELETE" ? "Radera" : 'Skriv "DELETE" för att aktivera'}
                >
                  Jag förstår — radera
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* --- Auto-logout notice (added) --- */}
        {showLogoutNotice && (
          <LogoutNotice
            seconds={10}
            onLoggedOut={() => {
              resetRegistrationSession();
              setShowLogoutNotice(false);
            }}
          />
        )}
        {CUSTOMER_DETAIL_MODAL}
    </div>
    </AuthGate>
  );
}
